/**
 * ============================================================================
 * CREATE CATEGORY MODAL v2.1 - BUTTON STRATEGY ALIGNMENT
 * ============================================================================
 * 
 * ✅ v2.1: BUTTON STRATEGY ALIGNMENT
 *          - .btn-primary: height:52px, radius:14px (L1 Primary)
 *          - .btn-secondary: height:44px, radius:12px (L2 Secondary)
 *          - .btn-danger: height:44px, radius:12px (L2 Secondary)
 *          - .modal-close: 32px + ::before 44px Touch (L4 Mini)
 * ✅ v2.0: Planivio Branding + Fix Typ-Feld
 *          - is_fixed (boolean) statt type (income/expense)
 *          - Teal/Green Farbschema
 * ✅ v1.0: Original
 * 
 * WICHTIG: Kategorien haben KEIN type-Feld (income/expense)!
 *          Der Transaktions-Typ wird bei der Transaktion selbst festgelegt.
 *          Kategorien haben nur is_fixed (Fix vs. Variable Kosten)
 * 
 * @version 2.1.0
 */

import React, { useState, useEffect } from 'react';

// Planivio Farben
const PLANIVIO_COLORS = {
  teal: '#4A90A4',
  green: '#6BA893',
  border: '#B8D4DC',
  bgLight: '#E8F4F8',
  bgCard: '#F0F9FF'
};

// Vordefinierte Icons für schnelle Auswahl
const QUICK_ICONS = [
  '🏠', '🚗', '💡', '📱', '🛒', '🍽️', '🎬', '🏥',
  '✈️', '👕', '🎁', '📚', '🏋️', '🐕', '💼', '💰'
];

// Vordefinierte Farben
const QUICK_COLORS = [
  '#4A90A4', '#6BA893', '#E8976C', '#EF4444', '#F59E0B',
  '#22C55E', '#3B82F6', '#8B5CF6', '#EC4899', '#6366F1'
];

function CreateCategoryModal({ show, onClose, editing, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    name: '',
    is_fixed: false,
    icon: '💰',
    color: PLANIVIO_COLORS.teal,
    monthly_limit: ''
  });

  // Reset form when modal opens/editing changes
  useEffect(() => {
    if (show) {
      if (editing) {
        setFormData({
          name: editing.name || '',
          is_fixed: editing.is_fixed === true,
          icon: editing.icon || '💰',
          color: editing.color || PLANIVIO_COLORS.teal,
          monthly_limit: editing.monthly_limit || ''
        });
      } else {
        setFormData({
          name: '',
          is_fixed: false,
          icon: '💰',
          color: PLANIVIO_COLORS.teal,
          monthly_limit: ''
        });
      }
    }
  }, [show, editing]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name.trim(),
      is_fixed: formData.is_fixed,
      icon: formData.icon,
      color: formData.color,
      monthly_limit: formData.monthly_limit ? parseFloat(formData.monthly_limit) : null
    };
    
    console.log('📝 Saving category:', data);
    onSave(data);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        
        <div className="modal-header">
          <h3>{editing ? '✏️ Kategorie bearbeiten' : '➕ Neue Kategorie'}</h3>
          <button onClick={onClose} className="modal-close">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            
            {/* Preview */}
            <div className="category-preview">
              <div 
                className="preview-icon"
                style={{ background: `${formData.color}20` }}
              >
                {formData.icon}
              </div>
              <div className="preview-info">
                <div className="preview-name">
                  {formData.name || 'Kategorie-Name'}
                </div>
                <div className="preview-type">
                  <span className={`type-badge ${formData.is_fixed ? 'fixed' : 'variable'}`}>
                    {formData.is_fixed ? '📌 Fixkosten' : '🔄 Variable Kosten'}
                  </span>
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="form-group">
              <label>Name *</label>
              <input 
                type="text" 
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required 
                placeholder="z.B. Lebensmittel, Miete, Gehalt..."
              />
            </div>

            {/* Typ (is_fixed) */}
            <div className="form-group">
              <label>Kostenart *</label>
              <div className="type-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${!formData.is_fixed ? 'active' : ''}`}
                  onClick={() => handleChange('is_fixed', false)}
                >
                  🔄 Variable Kosten
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${formData.is_fixed ? 'active' : ''}`}
                  onClick={() => handleChange('is_fixed', true)}
                >
                  📌 Fixkosten
                </button>
              </div>
              <div className="form-hint">
                {formData.is_fixed 
                  ? 'Fixkosten: Miete, Versicherungen, Abos - monatlich gleichbleibend'
                  : 'Variable Kosten: Lebensmittel, Shopping, Freizeit - schwanken monatlich'
                }
              </div>
            </div>

            {/* Icon */}
            <div className="form-group">
              <label>Icon *</label>
              <div className="icon-picker">
                <input 
                  type="text" 
                  value={formData.icon}
                  onChange={(e) => handleChange('icon', e.target.value)}
                  required 
                  placeholder="💰"
                  className="icon-input"
                />
                <div className="quick-icons">
                  {QUICK_ICONS.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      className={`quick-icon ${formData.icon === icon ? 'selected' : ''}`}
                      onClick={() => handleChange('icon', icon)}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Farbe */}
            <div className="form-group">
              <label>Farbe *</label>
              <div className="color-picker">
                <input 
                  type="color" 
                  value={formData.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="color-input"
                />
                <div className="quick-colors">
                  {QUICK_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`quick-color ${formData.color === color ? 'selected' : ''}`}
                      style={{ background: color }}
                      onClick={() => handleChange('color', color)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Monatslimit */}
            <div className="form-group">
              <label>Monatslimit (optional)</label>
              <div className="input-with-suffix">
                <input 
                  type="number" 
                  value={formData.monthly_limit}
                  onChange={(e) => handleChange('monthly_limit', e.target.value)}
                  step="0.01" 
                  min="0"
                  placeholder="z.B. 500"
                />
                <span className="suffix">€</span>
              </div>
              <div className="form-hint">
                Optional: Setze ein monatliches Budget-Limit für diese Kategorie
              </div>
            </div>

          </div>

          <div className="modal-footer">
            {editing && onDelete && (
              <button 
                type="button" 
                onClick={onDelete}
                className="btn-danger"
              >
                🗑️ Löschen
              </button>
            )}
            <div className="footer-right">
              <button 
                type="button" 
                onClick={onClose}
                className="btn-secondary"
              >
                Abbrechen
              </button>
              <button 
                type="submit"
                className="btn-primary"
                disabled={!formData.name.trim()}
              >
                💾 Speichern
              </button>
            </div>
          </div>
        </form>

      </div>

      {/* ✅ v2.1: Planivio Styles + Button Strategy Alignment */}
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }
        
        .modal {
          background: white;
          border-radius: 20px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease;
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .modal-header {
          padding: 1.25rem;
          border-bottom: 2px solid ${PLANIVIO_COLORS.border};
          background: ${PLANIVIO_COLORS.bgLight};
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 700;
          color: #1F2937;
        }
        
        /* ✅ v2.1: L4 Mini (32px visuell, 44px Touch via ::before) */
        .modal-close {
          width: 32px;
          height: 32px;
          border: none;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6B7280;
          transition: all 0.2s;
          position: relative;
          padding: 0;
          line-height: 1;
          box-sizing: border-box;
        }
        
        /* Touch-Target auf 44px erweitern */
        .modal-close::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
        
        .modal-close:hover {
          background: ${PLANIVIO_COLORS.border};
          color: #1F2937;
        }
        
        .modal-body {
          padding: 1.25rem;
          overflow-y: auto;
          max-height: calc(90vh - 180px);
        }
        
        /* Category Preview */
        .category-preview {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: ${PLANIVIO_COLORS.bgLight};
          border: 2px solid ${PLANIVIO_COLORS.border};
          border-radius: 12px;
          margin-bottom: 1.25rem;
        }
        
        .preview-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.75rem;
          flex-shrink: 0;
        }
        
        .preview-name {
          font-weight: 700;
          font-size: 1.1rem;
          color: #1F2937;
          margin-bottom: 0.25rem;
        }
        
        .type-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        
        .type-badge.fixed {
          background: rgba(74, 144, 164, 0.1);
          color: ${PLANIVIO_COLORS.teal};
        }
        
        .type-badge.variable {
          background: rgba(107, 168, 147, 0.1);
          color: ${PLANIVIO_COLORS.green};
        }
        
        /* Form Groups */
        .form-group {
          margin-bottom: 1rem;
        }
        
        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #1F2937;
        }
        
        .form-group input[type="text"],
        .form-group input[type="number"] {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid ${PLANIVIO_COLORS.border};
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.2s;
          box-sizing: border-box;
        }
        
        .form-group input:focus {
          outline: none;
          border-color: ${PLANIVIO_COLORS.teal};
          box-shadow: 0 0 0 3px rgba(74, 144, 164, 0.15);
        }
        
        .form-hint {
          margin-top: 0.5rem;
          font-size: 0.8rem;
          color: #6B7280;
        }
        
        /* Type Toggle (Segmented Control — Custom UI, nicht Button Strategy) */
        .type-toggle {
          display: flex;
          gap: 0.5rem;
        }
        
        .toggle-btn {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 2px solid ${PLANIVIO_COLORS.border};
          background: white;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          color: #6B7280;
        }
        
        .toggle-btn:hover {
          border-color: ${PLANIVIO_COLORS.teal};
        }
        
        .toggle-btn.active {
          background: linear-gradient(135deg, ${PLANIVIO_COLORS.teal} 0%, ${PLANIVIO_COLORS.green} 100%);
          border-color: transparent;
          color: white;
        }
        
        /* Icon Picker */
        .icon-picker {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .icon-input {
          width: 80px !important;
          text-align: center;
          font-size: 1.5rem !important;
        }
        
        .quick-icons {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        
        .quick-icon {
          width: 36px;
          height: 36px;
          border: 2px solid ${PLANIVIO_COLORS.border};
          background: white;
          border-radius: 8px;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .quick-icon:hover {
          border-color: ${PLANIVIO_COLORS.teal};
          background: ${PLANIVIO_COLORS.bgLight};
        }
        
        .quick-icon.selected {
          border-color: ${PLANIVIO_COLORS.teal};
          background: ${PLANIVIO_COLORS.bgLight};
          box-shadow: 0 0 0 2px rgba(74, 144, 164, 0.3);
        }
        
        /* Color Picker */
        .color-picker {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .color-input {
          width: 60px;
          height: 40px;
          padding: 4px;
          border: 2px solid ${PLANIVIO_COLORS.border};
          border-radius: 8px;
          cursor: pointer;
        }
        
        .quick-colors {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .quick-color {
          width: 32px;
          height: 32px;
          border: 2px solid transparent;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .quick-color:hover {
          transform: scale(1.1);
        }
        
        .quick-color.selected {
          border-color: #1F2937;
          box-shadow: 0 0 0 2px white, 0 0 0 4px #1F2937;
        }
        
        /* Input with Suffix */
        .input-with-suffix {
          position: relative;
        }
        
        .input-with-suffix input {
          width: 100%;
          padding-right: 2.5rem;
        }
        
        .input-with-suffix .suffix {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6B7280;
          font-weight: 600;
        }
        
        /* Modal Footer */
        .modal-footer {
          padding: 1rem 1.25rem;
          border-top: 2px solid ${PLANIVIO_COLORS.border};
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
        }
        
        .footer-right {
          display: flex;
          gap: 0.75rem;
          margin-left: auto;
          align-items: center;
        }
        
        /* ✅ v2.1: L1 Primary (52px, 14px radius) */
        .btn-primary {
          height: 52px;
          padding: 0 28px;
          background: linear-gradient(135deg, ${PLANIVIO_COLORS.teal} 0%, ${PLANIVIO_COLORS.green} 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 16px rgba(74, 144, 164, 0.25);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          line-height: 1;
          box-sizing: border-box;
          white-space: nowrap;
        }
        
        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(74, 144, 164, 0.4);
        }
        
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        /* ✅ v2.1: L2 Secondary (44px, 12px radius) */
        .btn-secondary {
          height: 44px;
          padding: 0 20px;
          background: white;
          color: #374151;
          border: 2px solid ${PLANIVIO_COLORS.border};
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          line-height: 1;
          box-sizing: border-box;
          white-space: nowrap;
        }
        
        .btn-secondary:hover {
          background: ${PLANIVIO_COLORS.bgLight};
          border-color: ${PLANIVIO_COLORS.teal};
        }
        
        /* ✅ v2.1: L2 Danger variant (44px, 12px radius) */
        .btn-danger {
          height: 44px;
          padding: 0 20px;
          background: white;
          color: #EF4444;
          border: 2px solid #FECACA;
          border-radius: 12px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          line-height: 1;
          box-sizing: border-box;
          white-space: nowrap;
        }
        
        .btn-danger:hover {
          background: #FEF2F2;
          border-color: #EF4444;
        }
        
        /* Dark Mode */
        .dark .modal {
          background: #1F2937;
        }
        
        .dark .modal-header {
          background: #374151;
          border-color: #4B5563;
        }
        
        .dark .modal-header h3,
        .dark .form-group label,
        .dark .preview-name {
          color: #F9FAFB;
        }
        
        .dark .modal-footer {
          border-color: #4B5563;
        }
        
        .dark .form-group input,
        .dark .toggle-btn,
        .dark .quick-icon,
        .dark .btn-secondary {
          background: #374151;
          border-color: #4B5563;
          color: #F9FAFB;
        }
        
        .dark .category-preview {
          background: #374151;
          border-color: #4B5563;
        }
      `}</style>
    </div>
  );
}

export default CreateCategoryModal;